import { Toast } from 'primereact/toast';
import React, { createContext, useCallback, useContext, useRef } from 'react';

type ToastContextType = {
  showToast: (
    severity?: 'success' | 'info' | 'warn' | 'error',
    summary?: string,
    detail?: string
  ) => void;
  /**
   * Show a toast with fully custom content (React node).
   * Note: PrimeReact's `content` property replaces the default message template —
   * the `summary` param is stored on the message but may not render visibly.
   * Include your own heading inside the `content` node if needed.
   */
  showToastWithContent: (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    content: React.ReactNode,
    life?: number
  ) => void;
  /** Dismisses all currently visible toasts. */
  clearToasts: () => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const toast = useRef<Toast>(null);

  const showToast = useCallback((
    severity: 'success' | 'info' | 'warn' | 'error' = 'error',
    summary: string = 'Error',
    detail: string | Error = ''
  ) => {
    let detailString: string;
    if (detail instanceof Error) {
      detailString = detail.message;
    } else if (typeof detail === 'string') {
      detailString = detail;
    } else if (detail && typeof detail === 'object') {
      detailString = JSON.stringify(detail);
    } else {
      detailString = String(detail || '');
    }

    if (toast?.current) {
      toast.current.show({
        severity,
        summary,
        detail: detailString,
      });
    }
  }, []);

  const showToastWithContent = useCallback((
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    content: React.ReactNode,
    life?: number
  ) => {
    if (toast?.current) {
      toast.current.show({
        severity,
        summary,
        content,
        life: life ?? 5000,
      });
    }
  }, []);

  const clearToasts = useCallback(() => {
    toast.current?.clear();
  }, []);

  return (
    <ToastContext.Provider
      value={{ showToast, showToastWithContent, clearToasts }}
    >
      <Toast ref={toast} />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
